/* ----------------------------------------------------------------------------
a globe of the world's land turning west to east, drawn on a <canvas> with
d3-geo's orthographic projection, which hides whatever is on the far side.
Canvas, not SVG: redrawing the land as an SVG path rebuilt a ~30KB string the
browser then had to parse and repaint, every frame; the canvas draws straight
from the projection.
<spinning-globe src="/assets/data/land.json"> fetches the land (a GeoJSON
geometry, from _data/world.js) the first time the globe is on screen. Its
color is the element's CSS color, read as it draws, so it follows the theme.
Decorative. Holds still for reduced motion, and pauses while off screen.
---------------------------------------------------------------------------- */
import { geoOrthographic, geoPath } from 'd3-geo';

const TILT = -20; // degrees: leans the north pole toward the viewer
const SECONDS_PER_TURN = 6; // in step with the footer logos, which loop every 5–6s
const FRAME = 1000 / 30; // the globe is small, so 30 frames a second is plenty
const OCEAN = 0.12; // how strongly the ocean is tinted with the land's color
const OUTLINE = 1.5; // CSS pixels

export default class SpinningGlobe extends HTMLElement {
	connectedCallback() {
		this.canvas = document.createElement('canvas');
		this.canvas.setAttribute('aria-hidden', 'true');
		this.replaceChildren(this.canvas);
		this.context = this.canvas.getContext('2d');

		// precision(0) skips d3's adaptive resampling, which adds points along
		// every edge so curves stay smooth on big maps; at this size it can't be
		// seen, and it was most of the cost of each frame
		this.projection = geoOrthographic().clipAngle(90).precision(0);
		this.path = geoPath(this.projection, this.context);
		this.angle = 0;
		this.world = null;

		this.frame = this.frame.bind(this);
		this.motion = window.matchMedia('(prefers-reduced-motion: no-preference)');
		this.handleMotionChange = () => this.update();
		this.motion.addEventListener('change', this.handleMotionChange);
		// a theme switch changes the color, maybe while the globe holds still
		this.themeObserver = new MutationObserver(() => this.draw());
		this.themeObserver.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class'],
		});
		this.resizeObserver = new ResizeObserver(() => this.resize());
		this.resizeObserver.observe(this);
		this.visible = false;
		this.observer = new IntersectionObserver(([entry]) => {
			this.visible = entry.isIntersecting;
			if (this.visible) this.load();
			this.update();
		});
		this.observer.observe(this);
	}

	disconnectedCallback() {
		cancelAnimationFrame(this.request);
		this.request = null;
		this.observer?.disconnect();
		this.resizeObserver?.disconnect();
		this.themeObserver?.disconnect();
		this.motion?.removeEventListener('change', this.handleMotionChange);
	}

	// match the canvas's pixels to its size on screen, so it stays sharp
	resize() {
		this.ratio = window.devicePixelRatio || 1;
		this.size = this.clientWidth;
		this.canvas.width = Math.round(this.size * this.ratio);
		this.canvas.height = Math.round(this.size * this.ratio);
		this.projection
			.scale(this.size / 2 - OUTLINE)
			.translate([this.size / 2, this.size / 2]);
		this.draw();
	}

	// fetch the land once; without it the globe shows only its ocean
	async load() {
		const src = this.getAttribute('src');
		if (this.loading || !src) return;
		this.loading = true;
		try {
			const response = await fetch(src);
			if (!response.ok) return;
			this.world = await response.json();
			this.draw();
		} catch {
			// offline or blocked: keep the empty globe
		}
	}

	// spin only while on screen and motion is welcome
	update() {
		const spin = this.visible && this.motion.matches;
		if (spin && !this.request) {
			this.last = null;
			this.request = requestAnimationFrame(this.frame);
		} else if (!spin && this.request) {
			cancelAnimationFrame(this.request);
			this.request = null;
		}
	}

	frame(time) {
		this.request = requestAnimationFrame(this.frame);
		if (this.last === null) this.last = time;
		const elapsed = time - this.last;
		if (elapsed < FRAME) return;
		this.last = time;
		this.angle =
			(this.angle + (elapsed / 1000) * (360 / SECONDS_PER_TURN)) % 360;
		this.draw();
	}

	draw() {
		if (!this.size) return;
		const { context, size } = this;
		const middle = size / 2;
		const radius = middle - OUTLINE;
		const color = getComputedStyle(this).color;
		context.setTransform(this.ratio, 0, 0, this.ratio, 0, 0);
		context.clearRect(0, 0, size, size);
		context.fillStyle = color;
		context.strokeStyle = color;

		// the ocean: the whole disc, lightly tinted
		context.globalAlpha = OCEAN;
		context.beginPath();
		context.arc(middle, middle, radius, 0, Math.PI * 2);
		context.fill();
		context.globalAlpha = 1;

		if (this.world) {
			this.projection.rotate([this.angle, TILT]);
			context.beginPath();
			this.path(this.world);
			context.fill();
		}

		context.lineWidth = OUTLINE;
		context.beginPath();
		context.arc(middle, middle, radius, 0, Math.PI * 2);
		context.stroke();
	}
}

window.customElements.define('spinning-globe', SpinningGlobe);
